import { useState, useEffect, useCallback } from 'react'
import { supabase, safeQuery } from '../lib/supabase'
import { localFallback, localUpsert, localDelete } from '../lib/syncEngine'
import { invalidate } from '../lib/dataInvalidation'
import { queuedWrite } from '../lib/queuedWrite'
import { getCached, setCached } from '../lib/queryCache'
import { collapseEmailDupes } from '../lib/callLogDupes'

// ─── Contact activity feed ────────────────────────────────────────────────────
// Matches call_log rows that reference this contact through ANY of:
//   1. contact_id equals this contact's id
//   2. linked_contacts JSONB array contains {id: contactId}
//   3. from_email equals any of the contact's known email addresses
//   4. to_emails JSONB array contains {email: any known address}
// The email-header fallback catches rows written by the Gmail sidebar
// where contact_id/linked_contacts may be null (e.g. thread walk writers
// that couldn't resolve the participants via the contacts table).
//
// Pass the contact's emails as the 2nd arg from the caller so we know
// which addresses to match on.
export function useContactCallLog(contactId, contactEmails = []) {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]   = useState(null)
  const emailsKey = (contactEmails || []).join(',')

  const fetch = useCallback(async () => {
    if (!contactId) { setLoading(false); return }

    // Stale-while-revalidate — show cached data instantly, fetch fresh silently
    const cacheKey = `call_log:contact:${contactId}:${emailsKey}`
    const stale = getCached(cacheKey)
    if (stale) {
      setEntries(stale)
      setLoading(false)
    } else {
      setLoading(true)
    }

    try {
      const linkedContactsJson = JSON.stringify([{ id: contactId }])

      // Build the set of queries to run in parallel
      const queries = [
        // 1. contact_id direct match
        safeQuery(
          supabase
            .from('call_log').select('*')
            .eq('contact_id', contactId)
            .order('contact_date', { ascending: false })
            .limit(200)
        ),
        // 2. linked_contacts JSONB containment — raw string avoids the
        //    supabase-js .contains() array-literal encoding bug
        safeQuery(
          supabase
            .from('call_log').select('*')
            .filter('linked_contacts', 'cs', linkedContactsJson)
            .order('contact_date', { ascending: false })
            .limit(200)
        ),
      ]
      // 3 + 4. email header fallbacks — one pair per known email address
      ;(contactEmails || []).forEach(email => {
        if (!email) return
        queries.push(
          safeQuery(
            supabase
              .from('call_log').select('*')
              .eq('from_email', email)
              .order('contact_date', { ascending: false })
              .limit(200)
          )
        )
        const toJson = JSON.stringify([{ email }])
        // to OR cc in one round-trip — cc was never matched before Phase 5,
        // so a contact who was only CC'd on a thread had zero timeline rows.
        queries.push(
          safeQuery(
            supabase
              .from('call_log').select('*')
              .or(`to_emails.cs.${toJson},cc_emails.cs.${toJson}`)
              .order('contact_date', { ascending: false })
              .limit(200)
          )
        )
      })

      const results = await Promise.all(queries)

      // Fail only if every query errored
      const allErrored = results.every(r => r.error)
      if (allErrored) {
        setError(results[0]?.error?.message || 'Query failed')
        setEntries([])
        return
      }

      // Dedupe by row id
      const seen = new Set()
      const merged = []
      results.forEach(r => {
        ;(r.data || []).forEach(row => {
          if (!row || seen.has(row.id)) return
          seen.add(row.id)
          merged.push(row)
        })
      })
      merged.sort((a, b) => new Date(b.contact_date) - new Date(a.contact_date))
      const collapsed = collapseEmailDupes(merged)
      setCached(cacheKey, collapsed)
      setEntries(collapsed)
    } catch (e) {
      setError(e.message)
      if (!stale) setEntries([])
    } finally {
      setLoading(false)
    }
  }, [contactId, emailsKey])

  useEffect(() => { fetch() }, [fetch])
  return { entries, loading, error, refetch: fetch }
}

// ─── Generic entity activity feed ────────────────────────────────────────────
// Works for deals / projects / properties / opportunities.
//
// Bug we're working around: supabase-js .contains() with a plain JS array
// generates a Postgres array literal ({...}) which does NOT match JSONB
// arrays stored as [...]. Rows write in two shapes historically:
//   - [{id:"uuid"}]   (object shape, new writers)
//   - ["uuid"]        (string shape, older writers)
// Instead of fighting the client, we use .filter(col, 'cs', rawJson) which
// sends the literal JSONB string to PostgREST — which correctly translates
// to the @> operator on the JSONB column. We issue both shapes in parallel
// and merge.
//
// This hook is the shared data source for every HUMAN-VISIBLE entity
// timeline (Deal/Project/Property/Opportunity Activity + PursuitTimeline) —
// per CLAUDE.md's iCloud section, personal mail stays "DISPLAY-ONLY, visible
// to Aaron" on those surfaces; it must never be filtered out here. (A prior
// pass added a source='icloud' exclusion at this query layer to protect
// Janet's entityContext, which over-applied the restriction to every human
// reader of this hook too.) Janet-blindness for iCloud-derived call_log
// content is enforced at the actual Janet-facing boundary instead — see
// `excludeIcloudEvents` in opportunityPursuit.js, applied where
// OpportunityDetailPage builds `entityContext` for Janet, not here.
function useEntityCallLog(columnName, entityId) {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetch = useCallback(async () => {
    if (!entityId || !columnName) { setLoading(false); return }

    // Stale-while-revalidate
    const cacheKey = `call_log:${columnName}:${entityId}`
    const stale = getCached(cacheKey)
    if (stale) {
      setEntries(stale)
      setLoading(false)
    } else {
      setLoading(true)
    }

    try {
      const objJson    = JSON.stringify([{ id: entityId }])
      const stringJson = JSON.stringify([entityId])

      // Joins the contact so classifiers downstream (e.g. buildPursuitTimeline
      // for opportunities) can attribute an event to a canonical contact —
      // without this, entry.contact is always undefined and stakeholder
      // selection has nothing to select.
      const [objResult, stringResult] = await Promise.all([
        safeQuery(
          supabase
            .from('call_log')
            .select('*, contact:contacts(id, first_name, last_name)')
            .filter(columnName, 'cs', objJson)
            .order('contact_date', { ascending: false })
            .limit(200)
        ),
        safeQuery(
          supabase
            .from('call_log')
            .select('*, contact:contacts(id, first_name, last_name)')
            .filter(columnName, 'cs', stringJson)
            .order('contact_date', { ascending: false })
            .limit(200)
        ),
      ])

      const errMsg =
        (objResult.error && stringResult.error)
          ? (objResult.error.message || stringResult.error.message)
          : null
      if (errMsg) { setError(errMsg); setEntries([]); return }

      // Dedupe by id, sort by date desc
      const seen = new Set()
      const merged = []
      ;[...(objResult.data || []), ...(stringResult.data || [])].forEach(r => {
        if (!r || seen.has(r.id)) return
        seen.add(r.id)
        merged.push(r)
      })
      merged.sort((a, b) => new Date(b.contact_date) - new Date(a.contact_date))
      const collapsed = collapseEmailDupes(merged)
      setCached(cacheKey, collapsed)
      setEntries(collapsed)
    } catch (e) {
      setError(e.message)
      if (!stale) setEntries([])
    } finally {
      setLoading(false)
    }
  }, [columnName, entityId])

  useEffect(() => { fetch() }, [fetch])
  return { entries, loading, error, refetch: fetch }
}

export function useDealCallLog(dealId)               { return useEntityCallLog('linked_deals',         dealId) }
export function useProjectCallLog(projectId)         { return useEntityCallLog('linked_projects',      projectId) }
export function usePropertyCallLog(propertyId)       { return useEntityCallLog('linked_properties',    propertyId) }
export function useOpportunityCallLog(opportunityId) { return useEntityCallLog('linked_opportunities', opportunityId) }

// ─── Company activity feed ────────────────────────────────────────────────────
// Most email/call activity never carries organization_id — the pipeline writers
// stamp contact_id (or just the email headers) and stop there. So a company's
// activity is the union of:
//   1. organization_id direct match
//   2. linked_companies JSONB containment (e.g. Granola meetings linked in review)
//   3. contact_id ∈ the company's contacts
//   4. from_email / to_emails matching those contacts' known addresses
// Edges 3+4 mirror useContactCallLog — without them a company page showed
// "No activity yet" while its contacts' pages each showed dozens of emails.
const COMPANY_FEED_MAX_CONTACTS = 50
const COMPANY_FEED_MAX_EMAILS   = 50

export function useCompanyCallLog(companyId) {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]   = useState(null)

  const fetch = useCallback(async () => {
    if (!companyId) { setLoading(false); return }

    // Stale-while-revalidate
    const cacheKey = `call_log:company:${companyId}`
    const stale = getCached(cacheKey)
    if (stale) {
      setEntries(stale)
      setLoading(false)
    } else {
      setLoading(true)
    }

    try {
      // Resolve the company's contacts first — their ids + emails are edges 3+4
      const { data: contactRows } = await safeQuery(
        supabase
          .from('contacts')
          .select('id, emails')
          .eq('organization_id', companyId)
          .limit(COMPANY_FEED_MAX_CONTACTS)
      )
      const contactIds = (contactRows || []).map(c => c.id)
      const contactEmails = [...new Set(
        (contactRows || [])
          .flatMap(c => Array.isArray(c.emails) ? c.emails : [])
          .map(e => e?.email)
          .filter(Boolean)
      )].slice(0, COMPANY_FEED_MAX_EMAILS)

      const queries = [
        // 1. organization_id direct match
        safeQuery(
          supabase
            .from('call_log')
            .select('*')
            .eq('organization_id', companyId)
            .order('contact_date', { ascending: false })
            .limit(200)
        ),
        // 2. linked_companies JSONB containment
        safeQuery(
          supabase
            .from('call_log')
            .select('*')
            .filter('linked_companies', 'cs', JSON.stringify([{ id: companyId }]))
            .order('contact_date', { ascending: false })
            .limit(200)
        ),
      ]
      // 3. via the company's contacts
      if (contactIds.length) {
        queries.push(
          safeQuery(
            supabase
              .from('call_log')
              .select('*')
              .in('contact_id', contactIds)
              .order('contact_date', { ascending: false })
              .limit(200)
          )
        )
      }
      // 4. via email headers — from_email in one query, to_emails containment per address
      if (contactEmails.length) {
        queries.push(
          safeQuery(
            supabase
              .from('call_log')
              .select('*')
              .in('from_email', contactEmails)
              .order('contact_date', { ascending: false })
              .limit(200)
          )
        )
        contactEmails.forEach(email => {
          const j = JSON.stringify([{ email }])
          queries.push(
            safeQuery(
              supabase
                .from('call_log')
                .select('*')
                // to OR cc in one round-trip — cc was never matched before
                // Phase 5, so a contact only CC'd produced zero company rows
                .or(`to_emails.cs.${j},cc_emails.cs.${j}`)
                .order('contact_date', { ascending: false })
                .limit(200)
            )
          )
        })
      }

      const results = await Promise.all(queries)
      if (results.every(r => r.error)) {
        setError(results[0]?.error?.message || 'Query failed')
        if (!stale) setEntries([])
        return
      }
      // Dedupe by id, sort by date desc
      const seen = new Set()
      const rows = []
      results.forEach(r => {
        ;(r.data || []).forEach(row => {
          if (!row || seen.has(row.id)) return
          seen.add(row.id)
          rows.push(row)
        })
      })
      rows.sort((a, b) => new Date(b.contact_date) - new Date(a.contact_date))
      const collapsed = collapseEmailDupes(rows)
      setCached(cacheKey, collapsed)
      setEntries(collapsed)
    } catch (e) {
      setError(e.message)
      if (!stale) setEntries([])
    } finally {
      setLoading(false)
    }
  }, [companyId])

  useEffect(() => { fetch() }, [fetch])
  return { entries, loading, error, refetch: fetch }
}

// ─── Full log with filters (ActivityPage) ────────────────────────────────────
export function useCallLog(filters = {}) {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]   = useState(null)
  const [total, setTotal]   = useState(0)

  const filterKey = JSON.stringify(filters)

  const fetch = useCallback(async () => {
    console.log('Fetching call_log...')
    setLoading(true)
    try {
      let q = supabase
        .from('call_log')
        .select('*, contact:contacts(first_name, last_name), organization:companies(name)', { count: 'estimated' })
        .order('contact_date', { ascending: false })

      if (filters.contact_id)           q = q.eq('contact_id', filters.contact_id)
      if (filters.organization_id)      q = q.eq('organization_id', filters.organization_id)
      if (filters.contact_type)         q = q.eq('contact_type', filters.contact_type)
      if (filters.exclude_contact_type) q = q.neq('contact_type', filters.exclude_contact_type)
      if (filters.exclude_contact_types?.length) q = q.not('contact_type', 'in', `(${filters.exclude_contact_types.join(',')})`)
      if (filters.direction)            q = q.eq('direction', filters.direction)
      if (filters.date_from)            q = q.gte('contact_date', filters.date_from)
      if (filters.date_to)              q = q.lte('contact_date', filters.date_to)
      if (filters.limit)                q = q.limit(filters.limit)

      const { data, error: err, count } = await safeQuery(q)
      if (err) { setError(err.message); setEntries([]); return }
      // Derive display names from joins, falling back to stored columns
      const enriched = (data || []).map(e => ({
        ...e,
        contact_name: e.contact_name || (e.contact ? `${e.contact.first_name} ${e.contact.last_name}`.trim() : null),
        organization_name: e.organization_name || e.organization?.name || null,
      }))
      setEntries(collapseEmailDupes(enriched))
      setTotal(count || 0)
    } catch (e) {
      setError(e.message)
      setEntries([])
    } finally {
      setLoading(false)
    }
  }, [filterKey]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetch() }, [fetch])
  return { entries, loading, error, total, refetch: fetch }
}

// ─── Follow-up queue (due today or overdue) ───────────────────────────────────
export function useFollowUpQueue() {
  const [queue, setQueue]   = useState([])
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async () => {
    console.log('Fetching call_log follow-up queue...')
    setLoading(true)
    try {
      const today = new Date().toISOString().split('T')[0]
      const { data, error: err } = await safeQuery(
        supabase
          .from('call_log')
          .select('*')
          .eq('follow_up_required', true)
          .lte('follow_up_date', today)
          .order('follow_up_date', { ascending: true })
      )
      if (err) { console.warn('useFollowUpQueue error:', err.message); setQueue([]); return }
      setQueue(data || [])
    } catch (e) {
      console.error('useFollowUpQueue:', e)
      setQueue([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetch() }, [fetch])
  return { queue, loading, refetch: fetch }
}

// ─── Daily + weekly counters (dashboard widget) ───────────────────────────────
export function useDailyCallCount() {
  const [count, setCount]       = useState(0)
  const [weekCount, setWeekCount] = useState(0)

  const fetch = useCallback(async () => {
    console.log('Fetching call_log daily count...')
    const todayStart = new Date(); todayStart.setHours(0,0,0,0)
    const weekStart  = new Date(); weekStart.setDate(weekStart.getDate() - weekStart.getDay()); weekStart.setHours(0,0,0,0)

    // Individual iMessages would inflate the count (a 50-text conversation
    // isn't 50 contacts) — texts and open-pings don't count as touches here
    const [todayRes, weekRes] = await Promise.all([
      safeQuery(
        supabase.from('call_log')
          .select('id', { count: 'estimated', head: true })
          .gte('contact_date', todayStart.toISOString())
          .not('contact_type', 'in', '(text,email_open)')
      ),
      safeQuery(
        supabase.from('call_log')
          .select('id', { count: 'estimated', head: true })
          .gte('contact_date', weekStart.toISOString())
          .not('contact_type', 'in', '(text,email_open)')
      ),
    ])
    setCount(todayRes.count || 0)
    setWeekCount(weekRes.count || 0)
  }, [])

  useEffect(() => { fetch() }, [fetch])
  return { count, weekCount, refetch: fetch }
}

// ─── CRUD ─────────────────────────────────────────────────────────────────────
export async function createCallLogEntry(data) {
  const result = await queuedWrite('call_log', 'insert', data)
  localUpsert('call_log', result).catch(() => {})
  invalidate('call_log', { id: result?.id, op: 'create' })
  return result
}

export async function updateCallLogEntry(id, data) {
  const result = await queuedWrite('call_log', 'update', data, id)
  localUpsert('call_log', result).catch(() => {})
  invalidate('call_log', { id, op: 'update' })
  return result
}

// ─── Phone-tag threads ──────────────────────────────────────────────────────
// An "open" thread = the most recent entry in a thread has an unresolved
// outcome (left_voicemail, no_answer, missed_call) and is within 30 days.

const UNRESOLVED_OUTCOMES = ['left_voicemail', 'no_answer', 'missed_call']

/** Find the most recent open thread for a contact (last 14 days). */
export async function findOpenThreadForContact(contactId) {
  if (!contactId) return null
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - 14)
  const { data } = await supabase
    .from('call_log')
    .select('id, thread_id, contact_id, contact_name, direction, outcome, summary, contact_date, created_at')
    .eq('contact_id', contactId)
    .gte('created_at', cutoff.toISOString())
    .order('created_at', { ascending: false })
    .limit(50)
  if (!data || data.length === 0) return null

  // Group by thread_id and find the latest entry per thread
  const threads = new Map()
  for (const row of data) {
    const tid = row.thread_id || row.id
    if (!threads.has(tid)) {
      threads.set(tid, { latest: row, count: 1 })
    } else {
      threads.get(tid).count++
    }
  }

  // Return the most recent thread whose latest entry is unresolved
  for (const [tid, info] of threads) {
    if (UNRESOLVED_OUTCOMES.includes(info.latest.outcome)) {
      return { thread_id: tid, latest: info.latest, attempt_count: info.count }
    }
  }
  return null
}

/** Fetch all open phone-tag threads via the SQL RPC. */
export function useOpenCallThreads() {
  const [threads, setThreads] = useState([])
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async () => {
    setLoading(true)
    try {
      const { data, error } = await safeQuery(supabase.rpc('get_open_call_threads'))
      if (error) throw error
      setThreads(data || [])
    } catch (e) {
      console.warn('useOpenCallThreads error:', e)
      setThreads([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetch() }, [fetch])

  return { threads, loading, refetch: fetch }
}

/** Fetch all entries in a single thread (chronological). */
export function useCallThread(threadId) {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async () => {
    if (!threadId) { setLoading(false); return }
    setLoading(true)
    const { data } = await safeQuery(
      supabase
        .from('call_log')
        .select('*')
        .eq('thread_id', threadId)
        .order('created_at', { ascending: true })
    )
    setEntries(data || [])
    setLoading(false)
  }, [threadId])

  useEffect(() => { fetch() }, [fetch])
  return { entries, loading, refetch: fetch }
}

export function isUnresolvedOutcome(outcome) {
  return UNRESOLVED_OUTCOMES.includes(outcome)
}

export async function deleteCallLogEntry(id) {
  await queuedWrite('call_log', 'delete', null, id)
  localDelete('call_log', id).catch(() => {})
  invalidate('call_log', { id, op: 'delete' })
}
